import React from "react";
import { pick } from "lodash";
import HelpTrigger from "@/components/HelpTrigger";
import Link from "@/components/Link";
import { Renderer as VisRenderer, Editor as VisEditor, updateVisualizationsSettings } from "@redash/viz/lib";
import { clientConfig } from "@/services/auth";

import countriesDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/countries.geo.json";
import usaDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/usa-albers.geo.json";
import subdivJapanDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/japan.prefectures.geo.json";
import franceDepartementsDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/france-departements.geo.json";
import saudiDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/saudi-regions.geo.json";
import algeriaDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/algeria-regions.geo.json";
import egyptDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/egypt-regions.geo.json";
import iraqDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/iraq-regions.geo.json";
import iranDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/iran-regions.geo.json";
import jordanDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/jordan-regions.geo.json";
import lebanonDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/lebanon-regions.geo.json";
import libyaDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/libya-regions.geo.json";
import moroccoDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/morocco-regions.geo.json";
import mauritaniaDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/mauritania-regions.geo.json";
import syriaDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/syria-regions.geo.json";
import tunisiaDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/tunisia-regions.geo.json";
import bahrainDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/bahrain-regions.geo.json";
import qatarDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/qatar-regions.geo.json";
import kuwaitDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/kuwait-regions.geo.json";
import omanDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/oman-regions.geo.json";
import uaeDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/uae-regions.geo.json";

function wrapComponentWithSettings(WrappedComponent) {
  return function VisualizationComponent(props) {
    updateVisualizationsSettings({
      HelpTriggerComponent: HelpTrigger,
      LinkComponent: Link,
      choroplethAvailableMaps: {
        countries: {
          name: "Countries",
          url: countriesDataUrl,
          fieldNames: {
            name: "Short name",
            name_long: "Full name",
            abbrev: "Abbreviated name",
            iso_a2: "ISO code (2 letters)",
            iso_a3: "ISO code (3 letters)",
            iso_n3: "ISO code (3 digits)",
          },
        },
        usa: {
          name: "USA",
          url: usaDataUrl,
          fieldNames: {
            name: "Name",
            ns_code: "National Standard ANSI Code (8-character)",
            geoid: "Geographic ID",
            usps_abbrev: "USPS Abbreviation",
            fips_code: "FIPS Code (2-character)",
          },  },
			france_departements: {
			  name: "France — Départements",
			  url: franceDepartementsDataUrl,
			  fieldNames: {
				code: "Code département",
				nom: "Nom",
			  },
			},
			
			saudi_regions: {
			  name: "Saudi Arabia — Regions",
			  url: saudiDataUrl,
			  fieldNames: {
				shapeName: "Region Name",
				shapeISO: "ISO Code (SA-04...)",
				shapeID: "Shape ID",
            region_code: "Region Code",
			  },
			},
			
			
			algeria_regions: {
          name: "Algeria — Regions",
          url: algeriaDataUrl,
          fieldNames: {
            shapeName: "Region Name",
            shapeISO: "ISO Code",
            region_code: "Region Code",
          },
        },
        egypt_regions: {
          name: "Egypt — Regions",
          url: egyptDataUrl,
          fieldNames: {
            shapeName: "Region Name",
            shapeISO: "ISO Code",
            region_code: "Region Code",
          },
        },
        iraq_regions: {
          name: "Iraq — Regions",
          url: iraqDataUrl,
          fieldNames: {
            shapeName: "Region Name",
            shapeISO: "ISO Code",
            region_code: "Region Code",
          },
        },
        iran_regions: {
          name: "Iran — Regions",
          url: iranDataUrl,
          fieldNames: {
            shapeName: "Region Name",
            shapeISO: "ISO Code",
            region_code: "Region Code",
          },
        },
        jordan_regions: {
          name: "Jordan — Regions",
          url: jordanDataUrl,
          fieldNames: {
            shapeName: "Region Name",
            shapeISO: "ISO Code",
            region_code: "Region Code",
          },
        },
        lebanon_regions: {
          name: "Lebanon — Regions",
          url: lebanonDataUrl,
          fieldNames: {
            shapeName: "Region Name",
            shapeISO: "ISO Code",
            region_code: "Region Code",
          },
        },
        libya_regions: {
          name: "Libya — Regions",
          url: libyaDataUrl,
          fieldNames: {
            shapeName: "Region Name",
            shapeISO: "ISO Code",
            region_code: "Region Code",
          },
        },
        morocco_regions: {
          name: "Morocco — Regions",
          url: moroccoDataUrl,
          fieldNames: {
            shapeName: "Region Name",
            shapeISO: "ISO Code",
            region_code: "Region Code",
          },
        },
        mauritania_regions: {
          name: "Mauritania — Regions",
          url: mauritaniaDataUrl,
          fieldNames: {
            shapeName: "Region Name",
            shapeISO: "ISO Code",
            region_code: "Region Code",
          },
        },
        syria_regions: {
          name: "Syria — Regions",
          url: syriaDataUrl,
          fieldNames: {
            shapeName: "Region Name",
            shapeISO: "ISO Code",
            region_code: "Region Code",
          },
        },
        tunisia_regions: {
          name: "Tunisia — Regions",
          url: tunisiaDataUrl,
          fieldNames: {
            shapeName: "Region Name",
            shapeISO: "ISO Code",
            region_code: "Region Code",
          },
        },
        bahrain_regions: {
          name: "Bahrain — Regions",
          url: bahrainDataUrl,
          fieldNames: {
            shapeName: "Region Name",
            shapeISO: "ISO Code",
            region_code: "Region Code",
          },
        },
        qatar_regions: {
          name: "Qatar — Municipalities (ADM1)",
          url: qatarDataUrl,
          fieldNames: {
            shapeName: "Municipality name",
            shapeISO: "ISO 3166-2 code (QA-…)",
            shapeID: "Shape ID",
            region_code: "Region Code",
          },
        },
        kuwait_regions: {
          name: "Kuwait — Governorates",
          url: kuwaitDataUrl,
          fieldNames: {
            shapeName: "Region Name",
            shapeISO: "ISO Code",
            region_code: "Region Code",
          },
        },
        oman_regions: {
          name: "Oman — Regions",
          url: omanDataUrl,
          fieldNames: {
            shapeName: "Region Name",
            shapeISO: "ISO Code (OM-…)",
            region_code: "Region Code",
          },
        },
        uae_regions: {
          name: "United Arab Emirates — Emirates",
          url: uaeDataUrl,
          fieldNames: {
            shapeName: "Emirate Name",
            shapeISO: "ISO Code (AE-…)",
            region_code: "Region Code",
          },
        },
        subdiv_japan: {
          name: "Japan/Prefectures",
          url: subdivJapanDataUrl,
          fieldNames: {
            name: "Name",
            name_alt: "Name (alternative)",
            name_local: "Name (local)",
            iso_3166_2: "ISO-3166-2",
            postal: "Postal Code",
            type: "Type",
            type_en: "Type (EN)",
            region: "Region",
            region_code: "Region Code",
          },
        },
      },
      ...pick(clientConfig, [
        "dateFormat",
        "dateTimeFormat",
        "integerFormat",
        "floatFormat",
        "booleanValues",
        "tableCellMaxJSONSize",
        "allowCustomJSVisualizations",
        "hidePlotlyModeBar",
      ]),
    });

    return <WrappedComponent {...props} />;
  };
}

export const Renderer = wrapComponentWithSettings(VisRenderer);
export const Editor = wrapComponentWithSettings(VisEditor);
